/**
 * Die Abstufungen der Markenfarbe — reine Rechnung, ohne DOM.
 *
 * ── Warum getrennt vom Store (28.07.2026) ──
 * Die Leiter stand in `appSettingsStore.ts`, zusammen mit `document`, `localStorage` und
 * `window`. Prüfen ließ sie sich damit nur mit einem DOM — und der Testlauf dieses
 * Projekts läuft im Backend, ohne DOM-Typen. Eine Farbrechnung braucht keinen Browser;
 * getrennt ist sie prüfbar, und der Store behält nur noch das Anwenden.
 *
 * ── Was hier gerechnet wird ──
 * Tailwind erwartet die Stufen 50…950. Sie entstehen, indem die Grundfarbe zu den hellen
 * Stufen hin aufgehellt und zu den dunklen hin abgedunkelt wird.
 *
 * Aufgehellt wird gegen WEISS — im dunklen Modus aber gegen den dunklen Grund der
 * Oberfläche. Ohne diese Unterscheidung ergab `bg-primary-50` auch im dunklen Modus
 * `#fefbf3`, also nahezu Weiß auf einer Seite mit `#0a0a0f` als Hintergrund. `index.css`
 * enthielt dafür zwar eine `.dark`-Regel mit `color-mix`, doch die konnte nie greifen:
 * Die Leiter wird INLINE auf `<html>` geschrieben, und ein Inline-Style schlägt jede
 * Regel aus einem Stylesheet.
 *
 * Die Grundfarbe selbst (Stufe 500) bleibt in beiden Modi unverändert — ein Firmenrot
 * darf im dunklen Modus nicht zu einem anderen Rot werden.
 */

/** negativ = Anteil des hellen Ziels, positiv = Anteil Schwarz. */
const STUFEN_MISCHUNG: Record<number, number> = {
  50: -0.95, 100: -0.9, 200: -0.75, 300: -0.55, 400: -0.28,
  500: 0,
  600: 0.14, 700: 0.32, 800: 0.46, 900: 0.58, 950: 0.76,
};

/** Der Grundton der dunklen Oberfläche — dieselbe Farbe, gegen die `index.css` mischt. */
const DUNKLER_GRUND = { r: 0x1a, g: 0x1a, b: 0x2e };

function mischeKanal(c: number, anteil: number, hellesZiel: number): number {
  const ziel = anteil < 0 ? hellesZiel : 0;
  return Math.round(c + (ziel - c) * Math.abs(anteil));
}

/**
 * Die vollständige Leiter als `{ '--color-primary-50': '#…', … }`.
 * Bei einer unbrauchbaren Farbe ein leeres Objekt — dann gelten die Rückfallwerte aus
 * `tailwind.config.js` weiter.
 */
export function primaerLeiter(hex: string, dunkel = false): Record<string, string> {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return {};
  const zahl = parseInt(m[1], 16);
  const r = (zahl >> 16) & 255, g = (zahl >> 8) & 255, b = zahl & 255;

  const leiter: Record<string, string> = {};
  for (const [stufe, anteil] of Object.entries(STUFEN_MISCHUNG)) {
    const rr = mischeKanal(r, anteil, dunkel ? DUNKLER_GRUND.r : 255);
    const gg = mischeKanal(g, anteil, dunkel ? DUNKLER_GRUND.g : 255);
    const bb = mischeKanal(b, anteil, dunkel ? DUNKLER_GRUND.b : 255);
    leiter[`--color-primary-${stufe}`] = '#' + [rr, gg, bb].map((v) => v.toString(16).padStart(2, '0')).join('');
  }
  return leiter;
}

/**
 * Ist der dunkle Modus aktiv? Reine Entscheidung — die Eingaben kommen von außen, damit
 * `localStorage` und `matchMedia` nicht in die Rechnung hineinreichen.
 *
 * @param gespeichert Wahl des Nutzers (`localStorage`), `null` = keine getroffen
 * @param systemDunkel Vorgabe des Betriebssystems
 */
export function dunkelModusAktiv(
  themeMode: 'light' | 'dark' | 'system' | undefined,
  allowDarkMode: boolean | undefined,
  gespeichert: string | null,
  systemDunkel: boolean
): boolean {
  // Ein abgeschalteter Dunkelmodus schlaegt alles — sonst haette der Betreiber den
  // Schalter, aber die Farben zoegen trotzdem mit.
  if (allowDarkMode === false) return false;
  if (themeMode === 'dark') return true;
  if (themeMode === 'light') return false;
  if (gespeichert !== null) return gespeichert === 'true';
  return systemDunkel;
}
